import { computed, watch } from 'vue'
import useGet from '../../../core/composables/api/useGet'
import { useRoute, useRouter } from 'vue-router'
import usePut from '../../../core/composables/api/usePut'
import { useI18n } from 'vue-i18n'
import { useSnackStore } from '../../../stores/snack.store'
import { ApiRequestStatus } from '../../../core/models/api/api-request-status'
import { SurveyState } from '../../survey/models/survey-state'
import type { PublicSurveyAccess, Survey, SurveyTandem } from '../models/survey'
import useDelete from '../../../core/composables/api/useDelete'
import { resolvePerspectiveLabel } from '@/core/models/perspective-label'
import { SELF_PERSPECTIVE_KEY, type Perspective } from '@/core/models/perspective'
import { iconForPerspective } from '@/core/models/feedback-perspective'
import { OpenFeedbackState } from '@/modules/open-feedbacks/composables/useOpenFeedbacks'

export type SurveyLinkItem = {
  access: PublicSurveyAccess
  url: string
  isPersonal: boolean
  memberName?: string
  numberOfFeedbacks: number
}

export const useSurvey = () => {
  const route = useRoute()
  const router = useRouter()

  const snackStore = useSnackStore()

  const { t } = useI18n()

  const templateId = route.params.surveyId as string

  const url = computed(() => {
    const templateId = route.params.surveyId as string
    return `survey/${templateId}`
  })

  const { data: survey, status: loadingStatus, load, reload } = useGet<Survey>(url)

  watch(
    () => route.params.surveyId,
    async () => {
      await load()
    },
    { immediate: true },
  )

  const updateUrl = computed(() => {
    if (!survey.value) {
      return ''
    }
    return `survey/${survey.value.surveyId}`
  })

  const { mutate: mutateFinishSurvey, status: statusFinishSurvey } = usePut<
    string,
    SurveyFinishBody
  >(updateUrl)

  const finishSurvey = async () => {
    if (survey.value.isOwner) {
      await mutateFinishSurvey({ id: survey.value.surveyId, status: SurveyState.finished })

      switch (statusFinishSurvey.value) {
        case ApiRequestStatus.Success:
          await reload()
          snackStore.showInfo(t('survey.finish.success'))
          break
        case ApiRequestStatus.Error:
          snackStore.showError(t('survey.finish.error'))
          break
      }
    }
  }

  const { remove: removeSurvey, status: statusDeleteSurvey } = useDelete<string>(
    `survey/${templateId}`,
  )

  const deleteSurvey = async () => {
    if (survey.value.isOwner) {
      await removeSurvey({})

      switch (statusDeleteSurvey.value) {
        case ApiRequestStatus.Success:
          router.push({ name: 'dashboard' })
          snackStore.showInfo(t('survey.delete.success'))
          break
        case ApiRequestStatus.Error:
          snackStore.showError(t('survey.delete.error'))
          break
      }
    }
  }

  const perspectiveCatalog = computed((): ReadonlyArray<Perspective> => {
    if (!survey.value) return []

    const byKey = new Map<string, Perspective>()
    for (const topic of survey.value.feedbackTopics) {
      for (const perspective of topic.perspectives) {
        if (!byKey.has(perspective.key)) {
          byKey.set(perspective.key, perspective)
        }
      }
    }
    return Array.from(byKey.values())
  })

  const selfFeedbackTandem = computed(() => {
    return survey.value.surveyTandems.find(
      (tandem) => tandem.perspectiveKey === SELF_PERSPECTIVE_KEY,
    )
  })

  /** Keys actually present on this survey (tandems + public link accesses). */
  const presentPerspectiveKeys = computed(() => {
    if (!survey.value) return [] as string[]

    const keys = new Set<string>()
    for (const tandem of survey.value.surveyTandems) {
      if (tandem.perspectiveKey) {
        keys.add(tandem.perspectiveKey)
      }
    }
    for (const access of survey.value.publicSurveyAccesses) {
      keys.add(access.perspectiveKey)
    }
    return Array.from(keys)
  })

  /**
   * Perspectives present on this survey: tandem/access keys resolved against
   * feedback-topic Perspective objects. Unknown keys are kept (fallback label).
   */
  const perspectives = computed((): ReadonlyArray<Perspective> => {
    const catalog = perspectiveCatalog.value
    return presentPerspectiveKeys.value.map((key) => {
      const fromTopic = catalog.find((p) => p.key === key)
      if (fromTopic) return fromTopic
      return {
        id: key,
        key,
        title: key,
        nameLong: key,
        nameShort: key,
        icon: 'groups',
        allowedMechanisms: [],
      }
    })
  })

  const resolveAccessUrl = (access: PublicSurveyAccess) => {
    const resolved = router.resolve({
      name: 'external-feedback',
      params: { token: access.token },
    })
    return `${window.location.origin}${resolved.fullPath}`
  }

  const surveyLinks = computed((): SurveyLinkItem[] => {
    if (!survey.value) return []

    const memberById = new Map(
      survey.value.surveyTandems
        .flatMap((tandem) => [tandem.sender, tandem.receiver])
        .filter((m): m is NonNullable<typeof m> => m != null)
        .map((m) => [m.id, m] as const),
    )

    return survey.value.publicSurveyAccesses.map((access) => {
      const member = access.memberId ? memberById.get(access.memberId) : undefined
      const surveyTandemsForPerspective = survey.value.surveyTandems.filter(
        (tandem) => tandem.perspectiveKey === access.perspectiveKey,
      )

      const numberOfFeedbacks = access.memberId
        ? surveyTandemsForPerspective.filter(
            (tandem) =>
              tandem.sender?.id === access.memberId && tandem.state === OpenFeedbackState.Completed,
          ).length
        : surveyTandemsForPerspective.filter(
            (tandem) => tandem.sender === undefined && tandem.state === OpenFeedbackState.Completed,
          ).length
      return {
        access,
        url: resolveAccessUrl(access),
        isPersonal: access.memberId != null,
        memberName: member ? `${member.firstName} ${member.lastName}` : undefined,
        numberOfFeedbacks: numberOfFeedbacks,
      }
    })
  })

  const perspectiveGroups = computed<PerspectiveGroup[]>(() => {
    if (!survey.value) return []

    const catalogOrder = new Map(
      perspectiveCatalog.value.map((perspective, index) => [perspective.key, index]),
    )

    return perspectives.value
      .filter((perspective) => perspective.key !== SELF_PERSPECTIVE_KEY)
      .map((perspective) => {
        const surveyTandems = survey.value.surveyTandems.filter(
          (tandem) => tandem.perspectiveKey === perspective.key,
        )
        const personalTandems = surveyTandems.filter((tandem) => !!tandem.sender)

        const surveyLinksForPerspective = surveyLinks.value.filter(
          (link) => link.access.perspectiveKey === perspective.key,
        )
        const numberOfFeedbacks = surveyTandems.filter(
          (tandem) => tandem.state === OpenFeedbackState.Completed,
        ).length

        const numberOfRequiredFeedbacks = surveyLinksForPerspective.some((link) => !link.isPersonal)
          ? undefined
          : personalTandems.length + surveyLinksForPerspective.length

        return {
          perspective: {
            perspectiveKey: perspective.key,
            label: resolvePerspectiveLabel(perspective.key, perspectiveCatalog.value, {
              selfLabel: t('perspectives.self.default'),
            }),
            icon: iconForPerspective(perspective),
          },
          numberOfFeedbacks,
          numberOfRequiredFeedbacks,
          personalTandems,
          surveyLinks: surveyLinksForPerspective,
        }
      })
      .filter((group) => group.personalTandems.length > 0 || group.surveyLinks.length > 0)
      .sort((a, b) => {
        const orderA = catalogOrder.get(a.perspective.perspectiveKey) ?? Number.MAX_SAFE_INTEGER
        const orderB = catalogOrder.get(b.perspective.perspectiveKey) ?? Number.MAX_SAFE_INTEGER
        return orderA - orderB
      })
  })

  return {
    survey,
    loadingStatus,
    finishSurvey,
    deleteSurvey,
    perspectives,
    perspectiveGroups,
    selfFeedbackTandem,
    reload,
  }
}

export interface PerspectiveGroup {
  perspective: {
    perspectiveKey: string
    label: string
    icon: string
  }
  personalTandems: SurveyTandem[]
  surveyLinks: SurveyLinkItem[]
  numberOfFeedbacks: number
  numberOfRequiredFeedbacks: number | undefined
}

interface SurveyFinishBody {
  readonly id: string
  readonly title?: string
  readonly status?: SurveyState
}
